import React, { useState, useEffect, useCallback } from 'react';
import {
  Beaker, Plus, X, FileText, Sparkles, RefreshCw, ChevronRight, ChevronDown,
  Microscope, Atom, Activity, Shield, FlaskConical, Trash2, AlertTriangle,
  ShieldAlert, BookOpen, ArrowLeft, Search, Globe, Download, Languages,
  Database, Check, Loader,
} from 'lucide-react';
import { canUse } from '../../lib/aiPermissions';
import { SEARCH_AI_BUILDER_ENABLED } from '../../lib/featureFlags';
import FederatedSearchPlanner from './federatedSearch/FederatedSearchPlanner';

/* ══════════════════════════════════════════════════════════════════════════
   ResearchTab — Phase F.3 (Research Workspace)

   Restricted to roles 'researcher', 'physician', 'admin', 'sysadmin'.
   ZignaGenix model: physicians are also researchers, so both roles get
   full workspace access. Renders a list of research sessions and a detail
   view per session. The detail view has
   tabs for the 6 sub-features (gap analysis, construct design, protocol
   draft, risk analysis, refinement, regulatory). Today only gap analysis
   is wired to AI; the rest are placeholder "Coming next phase" UI.

   AI tier: Opus 4.6 (research_* feature ids gated via aiPermissions).
   Documents and AI responses are encrypted at rest via the existing
   encryptPHI helper at the backend layer.
   ══════════════════════════════════════════════════════════════════════════ */

const TOOLS = [
  { id: 'gap_analysis',     label: 'Gap Analysis',     icon: Microscope,    color: '#A855F7',
    api: 'researchAiGapAnalysis',
    blurb: 'Synthesize literature + lab data + experiments into a structured map of current limitations and opportunities.',
    available: true },
  { id: 'construct_design', label: 'Construct Design', icon: Atom,          color: '#0EA5E9',
    blurb: 'Propose CAR architectures, IL-15 variants, and other molecular constructs with mechanistic rationale.',
    available: false, note: 'Requires AlphaFold/Rosetta integration for structure prediction. Reasoning-tier output available in next phase.' },
  { id: 'protocol_draft',   label: 'Protocol Synthesis ✨', icon: FlaskConical, color: '#10B981',
    api: 'researchAiSynthesizeProtocol',
    blurb: 'Synthesize a NEW draft protocol from evidence corpus + 35-center library precedents. Doctor or researcher initiates; AI proposes manufacturing, bioengineering, dosage, validation pathway.',
    available: true },
  { id: 'risk_analysis',    label: 'Risk Analysis',    icon: Shield,        color: '#F59E0B',
    blurb: 'Reason about toxicity (CRS/ICANS), persistence, antigen escape, and response stratification.',
    available: false, note: 'Coming in Phase F.3.5. Quantitative simulations (MD, response stratification models) require external compute.' },
  { id: 'refinement',       label: 'Iterative Refinement', icon: RefreshCw, color: '#0F766E',
    blurb: 'Feed experimental results back. AI analyzes discrepancies and proposes next iteration.',
    available: false, note: 'Coming in Phase F.3.6' },
  { id: 'regulatory_draft', label: 'Regulatory Drafting', icon: FileText,   color: '#7C3AED',
    blurb: 'Draft IND-enabling sections, SAP outlines, patent landscape summaries.',
    available: false, note: 'Coming in Phase F.3.7' },
];

const TOOL_BY_ARTIFACT_TYPE = Object.fromEntries(TOOLS.map(t => [t.id, t]));

const DOC_TYPES = [
  ['literature', 'Literature'],
  ['lab_data', 'Lab data'],
  ['experiment', 'Experiment notes'],
  ['trial_outcome', 'Trial outcome'],
  ['proprietary_dataset', 'Proprietary dataset'],
  ['other', 'Other'],
];

export default function ResearchTab() {
  const [resolvedRole, setResolvedRole] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await window.zignagenixAPI.getOwnProfile();
        if (!cancelled) setResolvedRole(me?.role || 'unknown');
      } catch { if (!cancelled) setResolvedRole('unknown'); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sessions tab: researcher/physician/admin/sysadmin. 'research' kept for backward compat.
  // Library tab broader — researcher + physician + admin + sysadmin (de-id'd protocols).
  const allowed = resolvedRole && ['research', 'researcher', 'physician', 'admin', 'sysadmin'].includes(resolvedRole);
  const libraryAllowed = resolvedRole && canUse(resolvedRole, 'research_protocol_library');
  const globalSourcesAllowed = resolvedRole && canUse(resolvedRole, 'research_source_search');
  const [view, setView] = useState('sessions');

  const reload = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listResearchSessions();
      setSessions(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  }, [allowed]);

  useEffect(() => { reload(); }, [reload]);

  if (resolvedRole === null) {
    return <div style={{ padding: 24, color: '#94A3B8', fontSize: 13 }}>Loading…</div>;
  }
  if (!allowed && !libraryAllowed) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <ShieldAlert size={32} color="#94A3B8" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
          Research Workspace is restricted
        </div>
        <div style={{ fontSize: 12, color: '#94A3B8', maxWidth: 420, margin: '0 auto' }}>
          Only users with role <strong>research</strong>, <strong>physician</strong>, <strong>admin</strong>, or <strong>sysadmin</strong> can access the proprietary research workspace.
          Contact your administrator if you need access.
        </div>
      </div>
    );
  }

  // Physician-only users see ONLY the Protocol Library view (no sessions access)
  if (!allowed && libraryAllowed) {
    return <ProtocolLibraryView role={resolvedRole} standalone />;
  }

  // Switch between sessions / library / global sources
  if (view === 'library') {
    return (
      <ProtocolLibraryView role={resolvedRole} onBack={() => setView('sessions')} />
    );
  }
  if (view === 'global_sources') {
    return (
      <GlobalSourcesView role={resolvedRole} onBack={() => setView('sessions')} />
    );
  }
  if (view === 'federated_search') {
    return <FederatedSearchPlanner role={resolvedRole} onBack={() => setView('sessions')} />;
  }

  if (selectedId !== null) {
    return (
      <ResearchSessionDetail
        sessionId={selectedId}
        role={resolvedRole}
        onBack={() => { setSelectedId(null); reload(); }}
      />
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{
        background: 'linear-gradient(135deg, #FAF5FF 0%, #F0F9FF 100%)',
        border: '1px solid #C4B5FD',
        borderRadius: 10, padding: 14, marginBottom: 16,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <Beaker size={18} color="#7C3AED" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: '#5B21B6', lineHeight: 1.5 }}>
          <strong>Research Workspace — proprietary, restricted.</strong> Use this space to synthesize literature,
          lab data, and trial outcomes into AI-assisted gap analyses, construct designs, and protocol drafts.
          All outputs are draft research aids — peer review and independent validation remain required before
          any clinical or manufacturing decision. Documents are encrypted at rest. Anonymize patient data before upload.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FlaskConical size={18} color="#A855F7" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>
            Research Sessions
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {globalSourcesAllowed && (
            <button onClick={() => setView('global_sources')} style={{
              padding: '7px 14px', borderRadius: 8, border: '1px solid #2563EB',
              background: '#EFF6FF', color: '#1D4ED8',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Globe size={13} /> Global Sources & Search
            </button>
          )}
          {SEARCH_AI_BUILDER_ENABLED && (
            <button onClick={() => setView('federated_search')} style={{
              padding: '7px 14px', borderRadius: 8, border: '1px solid #2563EB',
              background: '#EFF6FF', color: '#1D4ED8',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Search size={13} /> Federated Literature Search
            </button>
          )}
          {libraryAllowed && (
            <button onClick={() => setView('library')} style={{
              padding: '7px 14px', borderRadius: 8, border: '1px solid #14B8A6',
              background: '#F0FDFA', color: '#0F766E',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <BookOpen size={13} /> Browse Protocol Library
            </button>
          )}
          <button onClick={() => setShowCreate(true)} style={{
            padding: '7px 14px', borderRadius: 8, border: 'none',
            background: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
            color: '#FFF', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Plus size={13} /> New Research Session
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div style={{
          background: '#F8FAFC', border: '1px dashed #CBD5E1',
          borderRadius: 12, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            No research sessions yet.
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', maxWidth: 480, margin: '0 auto 14px' }}>
            Create a session for each research project (e.g., "CAR-NK metabolic fitness post IL-15 withdrawal").
            Attach literature, lab notes, and experimental data, then run AI tools to synthesize.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
          {sessions.map(s => (
            <button key={s.id} onClick={() => setSelectedId(s.id)} style={{
              textAlign: 'left', background: '#FFFFFF', border: '1px solid #E2E8F0',
              borderRadius: 10, padding: 14, cursor: 'pointer',
              transition: 'transform .1s, box-shadow .1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <FlaskConical size={13} color="#A855F7" />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>{s.name}</span>
                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase',
                  background: s.status === 'active' ? '#DCFCE7' : s.status === 'archived' ? '#FEF3C7' : '#F1F5F9',
                  color: s.status === 'active' ? '#15803D' : s.status === 'archived' ? '#B45309' : '#64748B',
                  fontWeight: 700 }}>{s.status}</span>
              </div>
              {s.target_indication && (
                <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>{s.target_indication}</div>
              )}
              {s.description && (
                <div style={{ fontSize: 11, color: '#64748B', marginBottom: 6, lineHeight: 1.4,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {s.description}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#94A3B8' }}>
                <span>{s.document_count} doc{s.document_count !== 1 ? 's' : ''}</span>
                <span>·</span>
                <span>{s.artifact_count} AI artifact{s.artifact_count !== 1 ? 's' : ''}</span>
                <span>·</span>
                <span>{new Date(s.updated_at).toLocaleDateString()}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateSessionModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); reload(); setSelectedId(id); }}
        />
      )}
    </div>
  );
}

/* ─── Create session modal ───────────────────────────────────────────────── */

function CreateSessionModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [targetIndication, setTargetIndication] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!name.trim()) { setError('Session name is required'); return; }
    setSaving(true); setError(null);
    try {
      const r = await window.zignagenixAPI.createResearchSession({
        name: name.trim(),
        targetIndication: targetIndication.trim() || null,
        description: description.trim() || null,
      });
      if (!r || r.success === false) { setError(r?.error || 'create failed'); return; }
      onCreated(r.sessionId);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <Overlay onClose={onClose}>
      <ModalHeader title="New Research Session" onClose={onClose} icon={FlaskConical} color="#A855F7" />
      <Field label="Session name *">
        <input value={name} onChange={e => setName(e.target.value)}
          placeholder='e.g., "CAR-NK metabolic fitness post IL-15 withdrawal"'
          style={inp} autoFocus />
      </Field>
      <Field label="Target indication / research direction">
        <input value={targetIndication} onChange={e => setTargetIndication(e.target.value)}
          placeholder='e.g., "Solid tumor infiltration in pancreatic cancer"' style={inp} />
      </Field>
      <Field label="Description (optional)">
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
          placeholder="High-level research framing, expert priorities, known constraints…"
          style={{ ...inp, resize: 'vertical' }} />
      </Field>
      {error && <Error msg={error} />}
      <Footer>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={save} disabled={saving || !name.trim()} style={{ ...btnPrimary, background: '#A855F7' }}>
          {saving ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : <><Plus size={12} /> Create</>}
        </button>
      </Footer>
    </Overlay>
  );
}

/* ─── Session detail view ────────────────────────────────────────────────── */

function ResearchSessionDetail({ sessionId, role, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTool, setActiveTool] = useState('gap_analysis');
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showImportCohort, setShowImportCohort] = useState(false);
  const [openArtifact, setOpenArtifact] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.getResearchSession(sessionId);
      setData(r && !r.error ? r : null);
    } finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { reload(); }, [reload]);

  // Poll for processing artifacts
  useEffect(() => {
    if (!data?.artifacts) return;
    const anyProcessing = data.artifacts.some(a => a.status === 'processing');
    if (!anyProcessing) return;
    const t = setTimeout(reload, 3000);
    return () => clearTimeout(t);
  }, [data?.artifacts, reload]);

  if (loading && !data) return <div style={{ padding: 24, fontSize: 12, color: '#94A3B8' }}>Loading session…</div>;
  if (!data) return <div style={{ padding: 24 }}><button onClick={onBack} style={btnGhost}>← Back</button> Session not found.</div>;

  return (
    <div style={{ padding: 16 }}>
      <button onClick={onBack} style={{ ...btnGhost, marginBottom: 12 }}>← Back to sessions</button>

      <div style={{
        background: 'linear-gradient(135deg, #FAF5FF 0%, #F0F9FF 100%)',
        border: '1px solid #C4B5FD', borderRadius: 10, padding: 14, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <FlaskConical size={16} color="#7C3AED" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#0F172A' }}>{data.name}</h2>
        </div>
        {data.target_indication && (
          <div style={{ fontSize: 12, color: '#5B21B6', marginBottom: 4 }}>
            <strong>Target:</strong> {data.target_indication}
          </div>
        )}
        {data.description && (
          <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>{data.description}</div>
        )}
        <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>
          Created by <strong>{data.created_by_name}</strong> on {new Date(data.created_at).toLocaleDateString()}
          {' · '}{(data.documents || []).length} document{(data.documents || []).length !== 1 ? 's' : ''}
          {' · '}{(data.artifacts || []).length} AI artifact{(data.artifacts || []).length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Documents row */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
            <FileText size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            Attached documents ({(data.documents || []).length})
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {canUse(role, 'research_cohort_import') && (
              <button onClick={() => setShowImportCohort(true)} style={{
                ...btnGhost, padding: '4px 10px', fontSize: 11,
                background: '#FAF5FF', borderColor: '#C4B5FD', color: '#7C3AED',
              }}>
                <Sparkles size={11} /> Import Patient Cohort
              </button>
            )}
            <button onClick={() => setShowAddDoc(true)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>
              <Plus size={11} /> Add document
            </button>
          </div>
        </div>
        {(data.documents || []).length === 0 ? (
          <div style={{ fontSize: 11, color: '#94A3B8', padding: '8px 12px', background: '#F8FAFC', borderRadius: 8 }}>
            No documents yet. Add literature, lab notes, or trial outcomes to enable AI synthesis.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
            {data.documents.map(d => (
              <DocumentCard key={d.id} doc={d} onRemoved={reload} />
            ))}
          </div>
        )}
      </div>

      {/* Tool selector + active tool panel */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          AI Research Tools
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6 }}>
          {TOOLS.map(t => {
            const Icon = t.icon;
            const isActive = activeTool === t.id;
            return (
              <button key={t.id} onClick={() => setActiveTool(t.id)} style={{
                padding: '8px 10px', borderRadius: 8, textAlign: 'left', cursor: 'pointer',
                border: `1px solid ${isActive ? t.color : t.color + '33'}`,
                background: isActive ? t.color + '15' : '#FFFFFF',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon size={12} color={t.color} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: t.color }}>{t.label}</span>
                  {!t.available && <span style={{ fontSize: 8, marginLeft: 'auto', color: '#94A3B8', textTransform: 'uppercase' }}>soon</span>}
                </div>
                <div style={{ fontSize: 9, color: '#64748B', marginTop: 3, lineHeight: 1.3 }}>{t.blurb}</div>
              </button>
            );
          })}
        </div>
      </div>

      <ToolPanel
        tool={TOOL_BY_ARTIFACT_TYPE[activeTool]}
        sessionId={sessionId}
        data={data}
        onCompleted={reload}
        onOpenArtifact={(id) => setOpenArtifact(id)}
      />

      {showAddDoc && (
        <AddDocumentModal
          sessionId={sessionId}
          onClose={() => setShowAddDoc(false)}
          onAdded={() => { setShowAddDoc(false); reload(); }}
        />
      )}

      {showImportCohort && (
        <ImportCohortModal
          sessionId={sessionId}
          onClose={() => setShowImportCohort(false)}
          onImported={() => { setShowImportCohort(false); reload(); }}
        />
      )}

      {openArtifact !== null && (
        <ArtifactDetailModal artifactId={openArtifact} onClose={() => setOpenArtifact(null)} />
      )}
    </div>
  );
}

/* ─── Tool panel ─────────────────────────────────────────────────────────── */

function ToolPanel({ tool, sessionId, data, onCompleted, onOpenArtifact }) {
  const Icon = tool.icon;
  const matchingArtifacts = (data.artifacts || []).filter(a => a.artifact_type === tool.id);

  if (!tool.available) {
    return (
      <div style={{
        background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 10,
        padding: 20, marginTop: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Icon size={16} color={tool.color} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{tool.label} — Coming next phase</span>
        </div>
        <div style={{ fontSize: 11, color: '#64748B', lineHeight: 1.5 }}>
          {tool.note || 'This module is queued for an upcoming phase. The permission gates and audit trail are already in place; the AI prompt + workflow will land in the next research-AI release.'}
        </div>
      </div>
    );
  }

  if (tool.id === 'gap_analysis') {
    return <GapAnalysisPanel
      sessionId={sessionId}
      data={data}
      artifacts={matchingArtifacts}
      onCompleted={onCompleted}
      onOpenArtifact={onOpenArtifact}
    />;
  }
  if (tool.id === 'protocol_draft') {
    return <ProtocolSynthesisPanel
      sessionId={sessionId}
      data={data}
      artifacts={matchingArtifacts}
      onCompleted={onCompleted}
      onOpenArtifact={onOpenArtifact}
    />;
  }
  return null;
}

function ProtocolSynthesisPanel({ sessionId, data, artifacts, onCompleted, onOpenArtifact }) {
  const [targetIndication, setTargetIndication] = useState('');
  const [clinicalContext, setClinicalContext] = useState('');
  const [designConstraints, setDesignConstraints] = useState('');
  const [priorAttempts, setPriorAttempts] = useState('');
  const [conditionCategory, setConditionCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    if (!targetIndication.trim()) {
      setError('Target indication is required');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const r = await window.zignagenixAPI.researchAiSynthesizeProtocol({
        sessionId,
        targetIndication: targetIndication.trim(),
        clinicalContext: clinicalContext.trim() || null,
        designConstraints: designConstraints.trim() || null,
        priorAttempts: priorAttempts.trim() || null,
        conditionCategory: conditionCategory || null,
      });
      if (!r || r.success === false) {
        setError(r?.error || 'failed to kick off');
        return;
      }
      setTargetIndication(''); setClinicalContext(''); setDesignConstraints(''); setPriorAttempts('');
      onCompleted();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        background: '#FFFFFF', border: '1px solid #10B981', borderRadius: 10,
        padding: 14, marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <FlaskConical size={14} color="#10B981" />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#15803D' }}>Synthesize New Protocol</span>
          <span style={{ fontSize: 9, padding: '1px 6px', marginLeft: 'auto', borderRadius: 4, background: '#F0FDFA',
            color: '#0F766E', textTransform: 'uppercase', fontWeight: 700 }}>Opus 4.6</span>
        </div>
        <div style={{ fontSize: 11, color: '#475569', marginBottom: 8, lineHeight: 1.4 }}>
          Doctor or researcher initiates: AI synthesizes a NEW draft protocol from the session corpus +
          de-identified library precedents from the 35-center network. Output includes manufacturing,
          bioengineering parameters, dosage regimen, patient selection, validation pathway, and risk
          profile — all evidence-tagged. <strong>Draft only — bench validation + expert review required.</strong>
        </div>
        <Field label="Target indication *">
          <input value={targetIndication} onChange={e => setTargetIndication(e.target.value)}
            placeholder={'e.g., "Autologous BMC-MSC for KL grade 3 medial knee OA with subchondral edema"'}
            style={inp} autoFocus />
        </Field>
        <Field label="Condition category (for library precedent lookup)">
          <select value={conditionCategory} onChange={e => setConditionCategory(e.target.value)} style={inp}>
            <option value="">— any —</option>
            {CONDITION_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Clinical context (optional)">
          <textarea value={clinicalContext} onChange={e => setClinicalContext(e.target.value)} rows={3}
            placeholder={'Patient profile, prior workup, why this protocol is being considered now…'}
            style={{ ...inp, resize: 'vertical' }} />
        </Field>
        <Field label="Design constraints / priorities (optional)">
          <textarea value={designConstraints} onChange={e => setDesignConstraints(e.target.value)} rows={3}
            placeholder={'e.g., "Avoid IL-15 systemic exposure", "Use only autologous cells", "Single-dose regimen preferred"'}
            style={{ ...inp, resize: 'vertical' }} />
        </Field>
        <Field label="What has been tried before (optional)">
          <textarea value={priorAttempts} onChange={e => setPriorAttempts(e.target.value)} rows={2}
            placeholder={'Prior approaches in literature or this clinic that did not achieve target outcomes.'}
            style={{ ...inp, resize: 'vertical' }} />
        </Field>
        {error && <Error msg={error} />}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={run} disabled={submitting || !targetIndication.trim()}
            style={{ ...btnPrimary, background: '#10B981' }}>
            {submitting ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</>
              : <><Sparkles size={12} /> Synthesize Protocol</>}
          </button>
        </div>
      </div>

      {artifacts.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Past protocol drafts ({artifacts.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {artifacts.map(a => (
              <ArtifactRow key={a.id} artifact={a} onOpen={() => onOpenArtifact(a.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GapAnalysisPanel({ sessionId, data, artifacts, onCompleted, onOpenArtifact }) {
  const [framing, setFraming] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    if ((data.documents || []).length === 0) {
      setError('Attach at least one document before running gap analysis.');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const r = await window.zignagenixAPI.researchAiGapAnalysis({
        sessionId,
        researcherFraming: framing.trim() || null,
      });
      if (!r || r.success === false) {
        setError(r?.error || 'failed to kick off');
        return;
      }
      setFraming('');
      onCompleted();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        background: '#FFFFFF', border: '1px solid #C4B5FD', borderRadius: 10,
        padding: 14, marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <Microscope size={14} color="#A855F7" />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED' }}>Run Gap Analysis</span>
          <span style={{ fontSize: 9, padding: '1px 6px', marginLeft: 'auto', borderRadius: 4, background: '#FAF5FF',
            color: '#7C3AED', textTransform: 'uppercase', fontWeight: 700 }}>Opus 4.6</span>
        </div>
        <div style={{ fontSize: 11, color: '#475569', marginBottom: 8, lineHeight: 1.4 }}>
          Synthesizes the {(data.documents || []).length} attached document{(data.documents || []).length !== 1 ? 's' : ''} into a structured map of current state, key limitations, and open opportunities. Each finding is tagged with its evidence basis (corpus vs prior knowledge vs inference). Output is JSON for downstream tooling.
        </div>
        <Field label="Researcher framing / priorities (optional)">
          <textarea value={framing} onChange={e => setFraming(e.target.value)} rows={3}
            placeholder={'e.g., "Prioritize IL-15 membrane-bound fusions that avoid CREM checkpoint suppression while preserving NKG2D expression."'}
            style={{ ...inp, resize: 'vertical' }} />
        </Field>
        {error && <Error msg={error} />}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={run} disabled={submitting || (data.documents || []).length === 0}
            style={{ ...btnPrimary, background: '#A855F7' }}>
            {submitting ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</> : <><Sparkles size={12} /> Run Gap Analysis</>}
          </button>
        </div>
      </div>

      {artifacts.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Past gap analyses ({artifacts.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {artifacts.map(a => (
              <ArtifactRow key={a.id} artifact={a} onOpen={() => onOpenArtifact(a.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ArtifactRow({ artifact, onOpen }) {
  const statusColor = artifact.status === 'completed' ? '#15803D'
    : artifact.status === 'processing' ? '#0EA5E9' : '#DC2626';
  return (
    <button onClick={onOpen} disabled={artifact.status === 'processing'}
      style={{
        textAlign: 'left', background: '#FFFFFF', border: '1px solid #E2E8F0',
        borderRadius: 8, padding: 10, cursor: artifact.status === 'processing' ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
      {artifact.status === 'processing' ? (
        <RefreshCw size={12} color={statusColor} style={{ animation: 'spin 1s linear infinite' }} />
      ) : artifact.status === 'completed' ? (
        <Sparkles size={12} color={statusColor} />
      ) : (
        <AlertTriangle size={12} color={statusColor} />
      )}
      <span style={{ fontSize: 12, color: '#0F172A', flex: 1, fontWeight: 600 }}>{artifact.title}</span>
      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase',
        background: statusColor + '22', color: statusColor, fontWeight: 700 }}>{artifact.status}</span>
      <span style={{ fontSize: 10, color: '#94A3B8' }}>
        {new Date(artifact.created_at).toLocaleString()}
      </span>
      {artifact.latency_ms && (
        <span style={{ fontSize: 10, color: '#94A3B8' }}>· {(artifact.latency_ms / 1000).toFixed(1)}s</span>
      )}
      <ChevronRight size={12} color="#94A3B8" />
    </button>
  );
}

/* ─── Artifact detail modal ──────────────────────────────────────────────── */

function ArtifactDetailModal({ artifactId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.zignagenixAPI.getResearchArtifact(artifactId);
        if (!cancelled) setData(r);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [artifactId]);

  function toggle(s) { setExpandedSection(p => ({ ...p, [s]: !p[s] })); }

  if (loading) return <Overlay onClose={onClose}><div style={{ padding: 20 }}>Loading…</div></Overlay>;
  if (!data || data.error) return <Overlay onClose={onClose}><div style={{ padding: 20 }}>Artifact not found</div></Overlay>;

  const tool = TOOL_BY_ARTIFACT_TYPE[data.artifact_type] || TOOLS[0];
  const Icon = tool.icon;
  const parsed = data.parsed_response;

  return (
    <Overlay onClose={onClose} wide>
      <ModalHeader title={data.title} onClose={onClose} icon={Icon} color={tool.color} />
      <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 12 }}>
        Generated by <strong>{data.created_by_name}</strong> · <code style={{ fontSize: 10 }}>{data.model_id}</code>
        {data.latency_ms && <> · {(data.latency_ms / 1000).toFixed(1)}s</>}
        {data.input_tokens && <> · {(data.input_tokens || 0) + (data.output_tokens || 0)} tokens</>}
      </div>

      {data.error_message && (
        <div style={{
          background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: 8,
          padding: 12, marginBottom: 12, fontSize: 12, color: '#991B1B',
        }}>
          <strong>Generation failed:</strong> {data.error_message}
        </div>
      )}

      {parsed?.summary && (
        <Section title="Summary" color={tool.color} open={true}>
          <div style={{ fontSize: 13, color: '#0F172A', lineHeight: 1.5 }}>{parsed.summary}</div>
        </Section>
      )}

      {Array.isArray(parsed?.current_state) && parsed.current_state.length > 0 && (
        <Section title={`Current state (${parsed.current_state.length})`}
          open={expandedSection.current_state ?? false}
          onToggle={() => toggle('current_state')} color="#10B981">
          {parsed.current_state.map((c, i) => (
            <div key={i} style={cardBox}>
              <div style={{ fontSize: 12, color: '#0F172A', marginBottom: 4 }}>{c.claim}</div>
              <div style={{ fontSize: 10, color: '#64748B' }}>
                <Badge label={c.evidence_basis} color={c.evidence_basis === 'corpus' ? '#15803D' : c.evidence_basis === 'prior_knowledge' ? '#0369A1' : '#B45309'} />
                {' '}
                <Badge label={`${c.confidence} confidence`} color={c.confidence === 'high' ? '#15803D' : c.confidence === 'moderate' ? '#B45309' : '#94A3B8'} />
              </div>
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(parsed?.key_limitations) && parsed.key_limitations.length > 0 && (
        <Section title={`Key limitations (${parsed.key_limitations.length})`}
          open={expandedSection.key_limitations ?? true}
          onToggle={() => toggle('key_limitations')} color="#DC2626">
          {parsed.key_limitations.map((l, i) => (
            <div key={i} style={{ ...cardBox, borderLeft: `3px solid ${l.severity === 'blocking' ? '#DC2626' : l.severity === 'moderate' ? '#F59E0B' : '#94A3B8'}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A', marginBottom: 3 }}>{l.limitation}</div>
              {l.mechanism_hypothesis && (
                <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>{l.mechanism_hypothesis}</div>
              )}
              <Badge label={l.severity} color={l.severity === 'blocking' ? '#DC2626' : l.severity === 'moderate' ? '#F59E0B' : '#94A3B8'} />
              {' '}
              <Badge label={l.evidence_basis} color={l.evidence_basis === 'corpus' ? '#15803D' : '#94A3B8'} />
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(parsed?.open_opportunities) && parsed.open_opportunities.length > 0 && (
        <Section title={`Open opportunities (${parsed.open_opportunities.length})`}
          open={expandedSection.open_opportunities ?? true}
          onToggle={() => toggle('open_opportunities')} color="#A855F7">
          {parsed.open_opportunities.map((o, i) => (
            <div key={i} style={{ ...cardBox, borderLeft: `3px solid ${o.priority === 'high' ? '#15803D' : o.priority === 'medium' ? '#F59E0B' : '#94A3B8'}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A', marginBottom: 3 }}>{o.opportunity}</div>
              {o.proposed_approach && (
                <div style={{ fontSize: 11, color: '#475569', marginBottom: 3 }}><strong>Approach:</strong> {o.proposed_approach}</div>
              )}
              {o.expected_outcome && (
                <div style={{ fontSize: 11, color: '#475569', marginBottom: 3 }}><strong>Success:</strong> {o.expected_outcome}</div>
              )}
              {Array.isArray(o.risk_factors) && o.risk_factors.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: 14, fontSize: 10, color: '#64748B' }}>
                  {o.risk_factors.map((r, j) => <li key={j}>{r}</li>)}
                </ul>
              )}
              <div style={{ marginTop: 4 }}><Badge label={`priority: ${o.priority}`} color={o.priority === 'high' ? '#15803D' : o.priority === 'medium' ? '#F59E0B' : '#94A3B8'} /></div>
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(parsed?.knowledge_gaps) && parsed.knowledge_gaps.length > 0 && (
        <Section title="Knowledge gaps" color="#F59E0B"
          open={expandedSection.knowledge_gaps ?? false}
          onToggle={() => toggle('knowledge_gaps')}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#0F172A' }}>
            {parsed.knowledge_gaps.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </Section>
      )}

      {Array.isArray(parsed?.next_recommended_reading) && parsed.next_recommended_reading.length > 0 && (
        <Section title="Next recommended reading" color="#0EA5E9"
          open={expandedSection.next_recommended_reading ?? false}
          onToggle={() => toggle('next_recommended_reading')}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#0F172A' }}>
            {parsed.next_recommended_reading.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </Section>
      )}

      {Array.isArray(parsed?.caveats) && parsed.caveats.length > 0 && (
        <Section title="Caveats" color="#94A3B8"
          open={expandedSection.caveats ?? false}
          onToggle={() => toggle('caveats')}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#475569' }}>
            {parsed.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </Section>
      )}
    </Overlay>
  );
}

/* ─── Document card + add modal ──────────────────────────────────────────── */

function DocumentCard({ doc, onRemoved }) {
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(false);

  async function remove(e) {
    e.stopPropagation();
    if (!window.confirm(`Remove document "${doc.title}"?`)) return;
    setRemoving(true);
    try {
      const r = await window.zignagenixAPI.removeResearchDocument(doc.id);
      if (r && r.success) onRemoved();
    } finally { setRemoving(false); }
  }

  const docTypeLabel = DOC_TYPES.find(([id]) => id === doc.doc_type)?.[1] || 'Other';

  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, padding: 10 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <FileText size={12} color="#64748B" />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#0F172A', flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</span>
        {expanded ? <ChevronDown size={11} color="#94A3B8" /> : <ChevronRight size={11} color="#94A3B8" />}
      </div>
      <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 3, display: 'flex', gap: 6 }}>
        <span style={{ background: '#F1F5F9', padding: '1px 5px', borderRadius: 3 }}>{docTypeLabel}</span>
        <span>{new Date(doc.added_at).toLocaleDateString()}</span>
        <span style={{ flex: 1 }} />
        <button onClick={remove} disabled={removing} title="Remove" style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#DC2626',
        }}><Trash2 size={11} /></button>
      </div>
      {expanded && (
        <div style={{
          fontSize: 11, color: '#475569', marginTop: 8, lineHeight: 1.5,
          maxHeight: 160, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          background: '#F8FAFC', padding: 8, borderRadius: 6,
        }}>{doc.content}</div>
      )}
    </div>
  );
}

/* ─── Import Patient Cohort modal (F.3 Integration A) ────────────────────── */

const CONDITION_CATEGORIES = [
  ['orthopedic', 'Orthopedic'],
  ['neurological', 'Neurological'],
  ['autoimmune', 'Autoimmune'],
  ['anti_aging', 'Anti-aging'],
  ['sexual_health', 'Sexual Health'],
  ['sports_injury', 'Sports Injury'],
  ['chronic_pain', 'Chronic Pain'],
  ['autism', 'Autism'],
  ['lyme', 'Lyme'],
  ['diabetes', 'Diabetes'],
  ['alzheimers', "Alzheimer's"],
  ['stroke_recovery', 'Stroke Recovery'],
  ['other', 'Other'],
];

/* ─── Protocol Library browser (de-identified successful protocols) ─────── */

const INTERVENTION_TYPES = [
  ['stem_cell', 'Stem Cell'],
  ['nk_cell', 'NK Cell'],
  ['exosome', 'Exosome'],
  ['peptide', 'Peptide'],
  ['supplement', 'Supplement'],
  ['prp', 'PRP'],
  ['other', 'Other'],
];

const OUTCOME_STATUS = [
  ['successful', 'Successful'],
  ['partial', 'Partial'],
  ['mixed', 'Mixed'],
  ['failed', 'Failed'],
  ['pending', 'Pending'],
];

function ProtocolLibraryView({ role, onBack, standalone }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchText, setSearchText] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listProtocolLibrary({ ...filters, searchText: searchText || undefined });
      setEntries(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  }, [filters, searchText]);

  useEffect(() => { reload(); }, [reload]);

  const isPhysicianOrAdmin = ['physician', 'admin', 'sysadmin'].includes(role);

  return (
    <div style={{ padding: 16 }}>
      <div style={{
        background: 'linear-gradient(135deg, #F0FDFA 0%, #FAF5FF 100%)',
        border: '1px solid #14B8A6', borderRadius: 10, padding: 14, marginBottom: 16,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <BookOpen size={18} color="#0F766E" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: '#134E4A', lineHeight: 1.5, flex: 1 }}>
          <strong>Research Protocol Library</strong> — de-identified successful protocols extracted from patient
          treatment outcomes. <strong>No PHI is visible</strong> to researchers; only clinical signals (condition,
          severity, age bucket, outcome). Published by physicians when a patient treatment plan reaches a notable
          outcome. Use this library to identify protocol patterns across patients without crossing into individual PHI.
        </div>
        {!standalone && (
          <button onClick={onBack} style={{ ...btnGhost }}>
            <ArrowLeft size={11} /> Back to Sessions
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
        <select value={filters.conditionCategory || ''} onChange={e => setFilters({ ...filters, conditionCategory: e.target.value })} style={inp}>
          <option value="">Any condition</option>
          {CONDITION_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.interventionType || ''} onChange={e => setFilters({ ...filters, interventionType: e.target.value })} style={inp}>
          <option value="">Any intervention</option>
          {INTERVENTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.outcomeStatus || ''} onChange={e => setFilters({ ...filters, outcomeStatus: e.target.value })} style={inp}>
          <option value="">Any outcome</option>
          {OUTCOME_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.severityLevel || ''} onChange={e => setFilters({ ...filters, severityLevel: e.target.value })} style={inp}>
          <option value="">Any severity</option>
          <option value="mild">Mild</option>
          <option value="moderate">Moderate</option>
          <option value="severe">Severe</option>
        </select>
        <input value={searchText} onChange={e => setSearchText(e.target.value)}
          placeholder="Search protocol name / outcome" style={inp} />
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading library…</div>
      ) : entries.length === 0 ? (
        <div style={{
          background: '#F8FAFC', border: '1px dashed #CBD5E1',
          borderRadius: 12, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            No protocols in the library matching your filters.
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', maxWidth: 480, margin: '0 auto' }}>
            Physicians publish successful patient treatment plans here. As your CRM accumulates real outcomes,
            this library will fill with de-identified protocols researchers can study.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {entries.map(e => {
            const outcomeColor = e.outcome_status === 'successful' ? '#15803D'
              : e.outcome_status === 'partial' ? '#F59E0B'
              : e.outcome_status === 'failed' ? '#DC2626'
              : '#94A3B8';
            return (
              <button key={e.id} onClick={() => setSelectedEntry(e.id)} style={{
                textAlign: 'left', background: '#FFFFFF', border: '1px solid #E2E8F0',
                borderRadius: 8, padding: 12, cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <FlaskConical size={13} color="#0F766E" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>{e.protocol_name}</span>
                  <span style={{
                    fontSize: 9, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase',
                    background: outcomeColor + '22', color: outcomeColor, fontWeight: 700,
                  }}>{e.outcome_status}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#475569', flexWrap: 'wrap' }}>
                  {e.intervention_type && <span><strong>Type:</strong> {e.intervention_type.replace(/_/g, ' ')}</span>}
                  {e.condition_category && <span>· <strong>Condition:</strong> {e.condition_category}{e.condition_subcategory ? ' / ' + e.condition_subcategory : ''}</span>}
                  {e.severity_level && <span>· {e.severity_level}</span>}
                  {e.patient_age_bucket && e.patient_age_bucket !== 'unknown' && <span>· age {e.patient_age_bucket}</span>}
                  {e.duration_weeks && <span>· {e.duration_weeks} wk</span>}
                  {isPhysicianOrAdmin && e.contributing_physician_name && (
                    <span>· by {e.contributing_physician_name}</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94A3B8' }}>
                    {new Date(e.published_at).toLocaleDateString()}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedEntry !== null && (
        <ProtocolLibraryDetailModal
          entryId={selectedEntry}
          isPhysicianOrAdmin={isPhysicianOrAdmin}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </div>
  );
}

function ProtocolLibraryDetailModal({ entryId, isPhysicianOrAdmin, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.zignagenixAPI.getProtocolLibraryEntry(entryId);
        if (!cancelled) setData(r && !r.error ? r : null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [entryId]);
  if (loading) return <Overlay onClose={onClose}><div style={{ padding: 20 }}>Loading…</div></Overlay>;
  if (!data) return <Overlay onClose={onClose}><div style={{ padding: 20 }}>Not found</div></Overlay>;

  return (
    <Overlay onClose={onClose} wide>
      <ModalHeader title={data.protocol_name} onClose={onClose} icon={FlaskConical} color="#0F766E" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: 12, color: '#475569' }}>
        <div><strong>Outcome:</strong> <Badge label={data.outcome_status} color={data.outcome_status === 'successful' ? '#15803D' : data.outcome_status === 'failed' ? '#DC2626' : '#F59E0B'} /></div>
        <div><strong>Intervention:</strong> {(data.intervention_type || '—').replace(/_/g, ' ')}</div>
        <div><strong>Condition:</strong> {data.condition_category || '—'}{data.condition_subcategory ? ' / ' + data.condition_subcategory : ''}</div>
        <div><strong>Severity:</strong> {data.severity_level || '—'}</div>
        <div><strong>Age bucket:</strong> {data.patient_age_bucket || 'unknown'}</div>
        <div><strong>Sex:</strong> {data.patient_sex || 'unknown'}</div>
        <div><strong>Clinic:</strong> {data.clinic_location || '—'}</div>
        <div><strong>Duration:</strong> {data.duration_weeks ? data.duration_weeks + ' weeks' : '—'}</div>
      </div>
      {Array.isArray(data.prior_treatments_tried) && data.prior_treatments_tried.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <FieldLbl>Prior treatments tried</FieldLbl>
          <div style={{ fontSize: 12, color: '#0F172A' }}>{data.prior_treatments_tried.join(', ')}</div>
        </div>
      )}
      {data.outcome_summary && (
        <div style={{ marginTop: 12 }}>
          <FieldLbl>Outcome summary (de-identified)</FieldLbl>
          <div style={{ fontSize: 12, color: '#0F172A', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{data.outcome_summary}</div>
        </div>
      )}
      {data.adverse_events && (
        <div style={{ marginTop: 12 }}>
          <FieldLbl>Adverse events</FieldLbl>
          <div style={{ fontSize: 12, color: '#7F1D1D' }}>{data.adverse_events}</div>
        </div>
      )}
      {data.intervention_details && (
        <div style={{ marginTop: 12 }}>
          <FieldLbl>Protocol details (JSON)</FieldLbl>
          <pre style={{
            fontSize: 10, background: '#F8FAFC', padding: 8, borderRadius: 6,
            maxHeight: 240, overflowY: 'auto', whiteSpace: 'pre-wrap',
          }}>{JSON.stringify(typeof data.intervention_details === 'string' ? JSON.parse(data.intervention_details) : data.intervention_details, null, 2)}</pre>
        </div>
      )}
      {isPhysicianOrAdmin && data.contributing_physician_name && (
        <div style={{ marginTop: 12, fontSize: 10, color: '#94A3B8' }}>
          Contributing physician: {data.contributing_physician_name} · Published {new Date(data.published_at).toLocaleString()}
        </div>
      )}
    </Overlay>
  );
}

function FieldLbl({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#475569',
                  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{children}</div>
  );
}

function ImportCohortModal({ sessionId, onClose, onImported }) {
  const [filters, setFilters] = useState({
    conditionCategory: '',
    conditionSubcategory: '',
    severityLevel: '',
    candidateStatus: '',
    clinicLocation: '',
    dateFromIso: '',
    dateToIso: '',
    limit: 500,
  });
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  function set(k, v) { setFilters(p => ({ ...p, [k]: v })); }

  async function run() {
    setImporting(true); setError(null);
    try {
      // Strip empties so backend WHERE only includes set filters
      const cleanFilters = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== '' && v !== null && v !== undefined) cleanFilters[k] = v;
      }
      const r = await window.zignagenixAPI.researchImportCohort({
        sessionId, filters: cleanFilters,
      });
      if (!r || r.success === false) { setError(r?.error || 'import failed'); return; }
      setResult(r);
    } catch (err) { setError(err.message); }
    finally { setImporting(false); }
  }

  return (
    <Overlay onClose={onClose} wide>
      <ModalHeader title="Import Anonymized Patient Cohort" onClose={onClose} icon={Sparkles} color="#7C3AED" />
      <div style={{
        padding: '8px 12px', borderRadius: 8, marginBottom: 14,
        background: '#FAF5FF', border: '1px solid #C4B5FD',
        fontSize: 11, color: '#5B21B6', lineHeight: 1.5,
      }}>
        Pulls an aggregate snapshot of patients matching your filters into the research session as a document.
        <strong> No individual PHI is exported</strong> — only de-identified counts, distributions, and age buckets.
        Action is audit-logged with your user id.
      </div>

      {!result ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Condition category">
              <select value={filters.conditionCategory} onChange={e => set('conditionCategory', e.target.value)} style={inp}>
                <option value="">— any —</option>
                {CONDITION_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Condition subcategory (free text)">
              <input value={filters.conditionSubcategory} onChange={e => set('conditionSubcategory', e.target.value)}
                placeholder="e.g., knee, parkinsons" style={inp} />
            </Field>
            <Field label="Severity">
              <select value={filters.severityLevel} onChange={e => set('severityLevel', e.target.value)} style={inp}>
                <option value="">— any —</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </Field>
            <Field label="Candidate status">
              <select value={filters.candidateStatus} onChange={e => set('candidateStatus', e.target.value)} style={inp}>
                <option value="">— any —</option>
                <option value="excellent">Excellent</option>
                <option value="possible">Possible</option>
                <option value="not_recommended">Not Recommended</option>
              </select>
            </Field>
            <Field label="Clinic location">
              <select value={filters.clinicLocation} onChange={e => set('clinicLocation', e.target.value)} style={inp}>
                <option value="">— any —</option>
                <option value="tijuana">Tijuana</option>
                <option value="cancun">Cancún</option>
                <option value="puerto_vallarta">Puerto Vallarta</option>
              </select>
            </Field>
            <Field label="Max records (1-2000)">
              <input type="number" value={filters.limit} min={1} max={2000}
                onChange={e => set('limit', parseInt(e.target.value, 10) || 500)} style={inp} />
            </Field>
            <Field label="Date from">
              <input type="date" value={filters.dateFromIso ? filters.dateFromIso.slice(0, 10) : ''}
                onChange={e => set('dateFromIso', e.target.value ? e.target.value + 'T00:00:00Z' : '')} style={inp} />
            </Field>
            <Field label="Date to">
              <input type="date" value={filters.dateToIso ? filters.dateToIso.slice(0, 10) : ''}
                onChange={e => set('dateToIso', e.target.value ? e.target.value + 'T23:59:59Z' : '')} style={inp} />
            </Field>
          </div>
          {error && <Error msg={error} />}
          <Footer>
            <button onClick={onClose} style={btnGhost}>Cancel</button>
            <button onClick={run} disabled={importing} style={{ ...btnPrimary, background: '#7C3AED' }}>
              {importing ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Importing…</>
                : <><Sparkles size={12} /> Import Cohort</>}
            </button>
          </Footer>
        </>
      ) : (
        <div>
          <div style={{
            padding: 14, borderRadius: 8, background: '#F0FDFA', border: '1px solid #14B8A6',
            marginBottom: 12,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0F766E', marginBottom: 6 }}>
              ✓ Imported successfully
            </div>
            <div style={{ fontSize: 11, color: '#134E4A', marginBottom: 8 }}>
              <strong>{result.cohortSize}</strong> matching contacts anonymized and attached as a new document.
            </div>
            <div style={{
              fontSize: 10, color: '#475569', whiteSpace: 'pre-wrap',
              background: '#FFFFFF', padding: 8, borderRadius: 4,
              maxHeight: 200, overflowY: 'auto',
            }}>{result.summaryPreview}</div>
          </div>
          <Footer>
            <button onClick={onImported} style={{ ...btnPrimary, background: '#7C3AED' }}>
              Done
            </button>
          </Footer>
        </div>
      )}
    </Overlay>
  );
}

function AddDocumentModal({ sessionId, onClose, onAdded }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [docType, setDocType] = useState('literature');
  const [sourceUrl, setSourceUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!title.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }
    setSaving(true); setError(null);
    try {
      const r = await window.zignagenixAPI.addResearchDocument({
        sessionId, title: title.trim(), content: content.trim(),
        docType, sourceUrl: sourceUrl.trim() || null,
      });
      if (!r || r.success === false) { setError(r?.error || 'add failed'); return; }
      onAdded();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <Overlay onClose={onClose} wide>
      <ModalHeader title="Add Document" onClose={onClose} icon={FileText} color="#A855F7" />
      <Field label="Title *">
        <input value={title} onChange={e => setTitle(e.target.value)}
          placeholder='e.g., "Liu et al 2024 NEJM — CAR-NK IL-15 persistence"' style={inp} autoFocus />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Document type">
          <select value={docType} onChange={e => setDocType(e.target.value)} style={inp}>
            {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Source URL (optional)">
          <input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
            placeholder="https://doi.org/..." style={inp} />
        </Field>
      </div>
      <Field label="Content (paste full text, abstract, notes, or summary) *">
        <textarea value={content} onChange={e => setContent(e.target.value)} rows={10}
          placeholder="Paste the document content here. Anonymize patient data before pasting."
          style={{ ...inp, resize: 'vertical' }} />
      </Field>
      {error && <Error msg={error} />}
      <Footer>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={save} disabled={saving || !title.trim() || !content.trim()}
          style={{ ...btnPrimary, background: '#A855F7' }}>
          {saving ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : <><Plus size={12} /> Add document</>}
        </button>
      </Footer>
    </Overlay>
  );
}

/* ─── Shared UI primitives ──────────────────────────────────────────────── */

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  paddingTop: '5vh', zIndex: 1000, overflowY: 'auto',
};
const modal = {
  background: '#FFFFFF', borderRadius: 12, padding: 20,
  width: 560, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
};
const modalWide = { ...modal, width: 760 };
const inp = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid #CBD5E1', fontSize: 13, fontFamily: 'inherit',
  outline: 'none',
};
const btnGhost = {
  padding: '6px 12px', borderRadius: 6, border: '1px solid #CBD5E1',
  background: '#FFFFFF', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
const btnPrimary = {
  padding: '7px 14px', borderRadius: 6, border: 'none',
  background: '#2E8B98', color: '#FFFFFF', fontSize: 12, fontWeight: 700,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
};
const cardBox = {
  background: '#FFFFFF', border: '1px solid #E2E8F0',
  borderRadius: 6, padding: 8, marginBottom: 6,
};

function Overlay({ children, onClose, wide }) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={wide ? modalWide : modal} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose, icon: Icon, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      <Icon size={16} color={color} />
      <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#0F172A', flex: 1 }}>{title}</h2>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
        <X size={16} color="#94A3B8" />
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function Footer({ children }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 14 }}>{children}</div>
  );
}

function Error({ msg }) {
  return (
    <div style={{
      background: '#FEE2E2', borderRadius: 6, padding: 8,
      fontSize: 11, color: '#991B1B', marginBottom: 10,
    }}>✗ {msg}</div>
  );
}

function Section({ title, color, children, open = false, onToggle }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 8px', borderRadius: 6, cursor: onToggle ? 'pointer' : 'default',
        background: color + '15', marginBottom: open ? 6 : 0,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color, flex: 1 }}>{title}</span>
        {onToggle && (open ? <ChevronDown size={11} color={color} /> : <ChevronRight size={11} color={color} />)}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, padding: '1px 6px',
      borderRadius: 4, background: color + '20', color, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{label}</span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Phase F.3.4 — Global Research Source Federation
   ══════════════════════════════════════════════════════════════════════════ */

function GlobalSourcesView({ role, onBack }) {
  const isAdmin = role === 'admin' || role === 'sysadmin';
  const [sources, setSources]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Session selection (required for import)
  const [availableSessions, setAvailableSessions] = useState([]);
  const [importSessionId, setImportSessionId]     = useState(null);

  // Search state
  const [query, setQuery]               = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [searching, setSearching]       = useState(false);
  const [results, setResults]           = useState([]);
  const [searchError, setSearchError]   = useState(null);

  // Load research sessions so users can pick where to import
  useEffect(() => {
    (async () => {
      try {
        const r = await window.zignagenixAPI.listResearchSessions();
        const list = Array.isArray(r) ? r : [];
        setAvailableSessions(list);
        if (list.length > 0) setImportSessionId(list[0].id);
      } catch { /* ignore — import will just be disabled */ }
    })();
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listResearchSources();
      const list = Array.isArray(r) ? r : [];
      setSources(list);
      // pre-select all implemented + enabled sources
      const defaults = list.filter(s => s.is_enabled && s.is_implemented).map(s => s.short_code);
      setSelectedCodes(prev => prev.length ? prev : defaults);
      setError(null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleCode = (code) => {
    setSelectedCodes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const runSearch = async () => {
    if (!query.trim() || selectedCodes.length === 0) return;
    setSearching(true); setSearchError(null); setResults([]);
    try {
      const r = await window.zignagenixAPI.researchFederatedSearch({
        query: query.trim(),
        sourceCodes: selectedCodes,
        limit: 25,
      });
      setResults(Array.isArray(r?.results) ? r.results : []);
      if (r?.errors?.length) {
        setSearchError(r.errors.map(e => `${e.source}: ${e.error}`).join(' · '));
      }
    } catch (e) {
      setSearchError(e?.message || String(e));
    } finally { setSearching(false); }
  };

  const publicSources    = sources.filter(s => !s.requires_license);
  const licensedSources  = sources.filter(s => s.requires_license);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={onBack} style={{
          background: 'none', border: '1px solid #CBD5E1', borderRadius: 6,
          padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 12, color: '#475569',
        }}>
          <ArrowLeft size={13} /> Back
        </button>
        <Globe size={18} color="#2563EB" />
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#0F172A' }}>
          Global Research Source Federation
        </h2>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)',
        border: '1px solid #BFDBFE', borderRadius: 10, padding: 14, marginBottom: 16,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <Database size={18} color="#1D4ED8" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: '#1E3A8A', lineHeight: 1.5 }}>
          <strong>Federated search across global research repositories.</strong> Query enabled
          sources in parallel, import open-access results into the active research session,
          and translate non-English documents to English on demand. Open-access sources are
          live today. Licensed/proprietary sources are registered but disabled pending
          BAA + API agreement.
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
          padding: 10, marginBottom: 12, fontSize: 12, color: '#B91C1C' }}>
          Failed to load sources: {error}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading sources…</div>
      ) : (
        <>
          {/* Search box */}
          <div style={{
            background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10,
            padding: 14, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', marginBottom: 8,
              display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={13} color="#2563EB" /> Federated query
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
                placeholder='e.g. "CAR-NK IL-15 persistence" or "stem cell knee osteoarthritis"'
                style={{
                  flex: 1, padding: '8px 10px', fontSize: 13, borderRadius: 6,
                  border: '1px solid #CBD5E1',
                }}
              />
              <button
                onClick={runSearch}
                disabled={searching || !query.trim() || selectedCodes.length === 0}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: searching || !query.trim() || selectedCodes.length === 0
                    ? '#CBD5E1'
                    : 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                  color: '#FFF', fontSize: 12, fontWeight: 700,
                  cursor: searching || !query.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {searching ? <Loader size={13} className="spin" /> : <Search size={13} />}
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>

            {/* Source selection chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11 }}>
              {sources.filter(s => s.is_enabled && s.is_implemented).map(s => {
                const sel = selectedCodes.includes(s.short_code);
                return (
                  <button key={s.short_code} onClick={() => toggleCode(s.short_code)} style={{
                    padding: '4px 10px', borderRadius: 12,
                    border: sel ? '1px solid #2563EB' : '1px solid #CBD5E1',
                    background: sel ? '#DBEAFE' : '#F8FAFC',
                    color: sel ? '#1D4ED8' : '#64748B',
                    fontWeight: sel ? 700 : 500, fontSize: 11, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {sel && <Check size={11} />}
                    {s.name}
                  </button>
                );
              })}
            </div>

            {searchError && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#B45309',
                background: '#FFFBEB', border: '1px solid #FDE68A',
                borderRadius: 6, padding: 8 }}>
                {searchError}
              </div>
            )}
          </div>

          {/* Session selector for imports */}
          {results.length > 0 && (
            <div style={{
              background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
              padding: 10, marginBottom: 12, fontSize: 12, display: 'flex',
              alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{ color: '#92400E', fontWeight: 600 }}>Import target session:</span>
              {availableSessions.length === 0 ? (
                <span style={{ color: '#B45309' }}>No research sessions found — create one first to enable imports.</span>
              ) : (
                <select
                  value={importSessionId || ''}
                  onChange={e => setImportSessionId(parseInt(e.target.value, 10))}
                  style={{
                    padding: '4px 8px', fontSize: 12, borderRadius: 5,
                    border: '1px solid #FDE68A', background: '#FFFFFF',
                  }}>
                  {availableSessions.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Search results */}
          {results.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>
                {results.length} result{results.length === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {results.map((r, i) => (
                  <SearchResultRow
                    key={`${r.source_code}-${r.external_id || i}`}
                    result={r}
                    importSessionId={importSessionId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Source registry — public */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <Globe size={14} color="#10B981" /> Open-access sources ({publicSources.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 8, marginBottom: 18 }}>
            {publicSources.map(s => (
              <SourceCard key={s.short_code} source={s} isAdmin={isAdmin} onChanged={reload} />
            ))}
          </div>

          {/* Source registry — licensed (disabled) */}
          {licensedSources.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={14} color="#94A3B8" /> Licensed sources ({licensedSources.length})
                <span style={{ fontSize: 10, fontWeight: 500, color: '#94A3B8' }}>
                  — registered, pending license + BAA
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                {licensedSources.map(s => (
                  <SourceCard key={s.short_code} source={s} isAdmin={isAdmin} onChanged={reload} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}

function SourceCard({ source, isAdmin, onChanged }) {
  const [saving, setSaving] = useState(false);
  const enabled = !!source.is_enabled;
  const implemented = !!source.is_implemented;

  const toggle = async () => {
    if (!isAdmin || saving) return;
    setSaving(true);
    try {
      const r = await window.zignagenixAPI.updateResearchSource({
        id: source.id,
        is_enabled: !enabled,
      });
      if (r && r.success === false) throw new Error(r.error || 'update failed');
      onChanged?.();
    } catch (e) {
      alert(`Update failed: ${e?.message || e}`);
    } finally { setSaving(false); }
  };

  const status = !implemented
    ? { label: 'Coming soon', color: '#94A3B8', bg: '#F1F5F9' }
    : enabled
      ? { label: 'Live', color: '#15803D', bg: '#DCFCE7' }
      : { label: 'Disabled', color: '#B45309', bg: '#FEF3C7' };

  return (
    <div style={{
      background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10,
      padding: 12, display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>{source.name}</div>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>
            {source.region} · {(source.primary_language || 'EN').toUpperCase()} · {source.source_type}
          </div>
        </div>
        <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, fontWeight: 700,
          textTransform: 'uppercase', background: status.bg, color: status.color }}>
          {status.label}
        </span>
      </div>
      {source.description && (
        <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.4 }}>
          {source.description}
        </div>
      )}
      {source.license_status && source.requires_license && (
        <div style={{ fontSize: 10, color: '#7C3AED', fontWeight: 600 }}>
          License: {source.license_status}
        </div>
      )}
      {isAdmin && implemented && (
        <button onClick={toggle} disabled={saving} style={{
          marginTop: 4, padding: '5px 10px', borderRadius: 6,
          border: '1px solid ' + (enabled ? '#FECACA' : '#BBF7D0'),
          background: enabled ? '#FEF2F2' : '#F0FDF4',
          color: enabled ? '#B91C1C' : '#15803D',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
        }}>
          {saving ? '…' : enabled ? 'Disable source' : 'Enable source'}
        </button>
      )}
    </div>
  );
}

function SearchResultRow({ result, importSessionId }) {
  const [expanded, setExpanded]   = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported]   = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [error, setError]         = useState(null);

  const isNonEnglish = result.language && result.language.toLowerCase().slice(0, 2) !== 'en';

  const doImport = async () => {
    if (!importSessionId) {
      setError('Select a target research session before importing.');
      return;
    }
    setImporting(true); setError(null);
    try {
      const authorsStr = Array.isArray(result.authors) ? result.authors.join(', ') : (result.authors || null);
      const r = await window.zignagenixAPI.importResearchResult({
        sessionId: importSessionId,
        sourceId: result.source_id,
        sourceName: result.source_name,
        externalId: result.external_id,
        title: result.title,
        authors: authorsStr,
        publicationDate: result.publication_date,
        abstract: result.abstract,
        fullTextUrl: result.full_text_url,
        language: result.language || 'en',
      });
      if (r && r.success === false) throw new Error(r.error || 'import failed');
      setImported(true);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setImporting(false); }
  };

  const doTranslate = async () => {
    if (!result.abstract) return;
    setTranslating(true); setError(null);
    try {
      const r = await window.zignagenixAPI.translateResearchDocument({
        text: result.abstract,
        sourceLang: (result.language || 'auto').toLowerCase().slice(0, 2),
      });
      if (r && r.success === false) throw new Error(r.error || 'translate failed');
      setTranslation(r?.translatedText || '');
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setTranslating(false); }
  };

  return (
    <div style={{
      background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 3 }}>
            {result.title || '(untitled)'}
          </div>
          <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 4 }}>
            {result.source_name || result.source_code}
            {result.journal && ` · ${result.journal}`}
            {result.publication_date && ` · ${result.publication_date}`}
            {result.language && ` · ${result.language.toUpperCase()}`}
            {result.authors && ` · ${Array.isArray(result.authors) ? result.authors.slice(0, 3).join(', ') : result.authors}`}
          </div>
          {result.abstract && (
            <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.4,
              maxHeight: expanded ? 'none' : 60, overflow: 'hidden',
              maskImage: expanded ? 'none' : 'linear-gradient(180deg, #000 60%, transparent)',
              WebkitMaskImage: expanded ? 'none' : 'linear-gradient(180deg, #000 60%, transparent)' }}>
              {result.abstract}
            </div>
          )}
          {translation && (
            <div style={{ marginTop: 6, padding: 8, background: '#F0F9FF',
              borderRadius: 6, border: '1px solid #BFDBFE', fontSize: 11, color: '#1E3A8A',
              lineHeight: 1.4 }}>
              <div style={{ fontWeight: 700, marginBottom: 3, fontSize: 10, textTransform: 'uppercase',
                letterSpacing: 0.3 }}>
                English translation
              </div>
              {translation}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {result.abstract && (
          <button onClick={() => setExpanded(!expanded)} style={{
            padding: '4px 10px', borderRadius: 5, border: '1px solid #CBD5E1',
            background: '#F8FAFC', color: '#475569',
            fontSize: 10, fontWeight: 600, cursor: 'pointer',
          }}>
            {expanded ? 'Collapse' : 'Read abstract'}
          </button>
        )}
        {result.full_text_url && (
          <a href={result.full_text_url} target="_blank" rel="noopener noreferrer" style={{
            padding: '4px 10px', borderRadius: 5, border: '1px solid #CBD5E1',
            background: '#F8FAFC', color: '#475569',
            fontSize: 10, fontWeight: 600, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Download size={11} /> Open at source
          </a>
        )}
        {isNonEnglish && result.abstract && !translation && (
          <button onClick={doTranslate} disabled={translating} style={{
            padding: '4px 10px', borderRadius: 5, border: '1px solid #BFDBFE',
            background: '#EFF6FF', color: '#1D4ED8',
            fontSize: 10, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Languages size={11} /> {translating ? 'Translating…' : `Translate ${result.language?.toUpperCase()}→EN`}
          </button>
        )}
        <button onClick={doImport} disabled={importing || imported} style={{
          padding: '4px 10px', borderRadius: 5, border: '1px solid ' + (imported ? '#BBF7D0' : '#A855F7'),
          background: imported ? '#F0FDF4' : '#FAF5FF',
          color: imported ? '#15803D' : '#7C3AED',
          fontSize: 10, fontWeight: 700,
          cursor: importing || imported ? 'default' : 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          {imported ? <><Check size={11} /> Imported</> : <><Plus size={11} /> {importing ? 'Importing…' : 'Import to library'}</>}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#B91C1C' }}>{error}</div>
      )}
    </div>
  );
}
